import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Paper,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import {
  formatCurrency,
  formatNumber,
  validateTextField,
} from "../../../../../../../../utils/helpers";
import { useSearchParams } from "react-router-dom";
import { setNewScopeLoading } from "../../../../../../../../features/scopesTabSlice";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { GridColDef } from "@mui/x-data-grid-pro";
import { STOperationEndPointLogic } from "../../../../../../../../logic/PaidTrafficManagement/SearchTermOperation/searchTermOperationEndPointLogic";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../../../features/userSlice";
import { useScopes } from "../../../../STOperationhooks/useScopes";
import CommonDataGridTable from "../../../../CommonDataGridTable/CommonDataGridTable";
import { intersection, not } from "../../../../utilsSearchTerm/helperST";

interface BulkActionModalProps {
  open: boolean;
  onClose: () => void;
}

const AddScopeModal = ({ open, onClose }: BulkActionModalProps) => {
  const dispatch = useDispatch();
  const [scopeName, setScopeName] = useState("");
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [campaignError, setCampaignError] = useState("");

  // --- Transfer List state ---
  const [checked, setChecked] = useState<number[]>([]);
  const [left, setLeft] = useState<any[]>([]);
  const [right, setRight] = useState<any[]>([]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const {
    campaignsWithoutScopeData,
    newScopeLoading,
    editScopeLoading,
    campaignToScopeLoading,
  } = useSelector((state: any) => state.searchTermOperation);
  const { scopeList, linkCampaignToScope } = useScopes();

  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const ad_account_shared_uid = searchParams.get("account") || ""; // account shared_uid from URL

  const filteredCampaigns = (campaignsWithoutScopeData ?? []).map(
    (campaign: any) => {
      return {
        campaign_shared_uid: campaign.campaign_shared_uid,
        campaign_name: campaign.campaign_name,
      };
    }
  );

  //get campaign details for the campaigns in left list
  const campaignsWithDetails =
    (campaignsWithoutScopeData?.length ?? 0) > 0
      ? campaignsWithoutScopeData.map((details: any) => ({
          id: uuidv4(),
          campaign_shared_uid: details.campaign_shared_uid || "",
          campaign_name: details.campaign_name || "",
          campaign_id: details.campaign_id || "",
          clicks_last_30_days: details.clicks_last_30_days || "",
          cost_last_30_days: details.cost_last_30_days || "",
          convertions_last_30_days: details.convertions_last_30_days || "",
        }))
      : [];

  //--when modal open set left with all campaigns and right empty
  useEffect(() => {
    if (open === true && filteredCampaigns.length > 0) {
      setLeft(filteredCampaigns); // set left with all campaigns
      setRight([]); // empty right
    }
    //eslint-disable-next-line
  }, [open]);

  const handleToggle = (id: number) => () => {
    const currentIndex = checked.indexOf(id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(id);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setRight([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(
      not(
        checked,
        leftChecked.map((i) => i.campaign_shared_uid)
      )
    );
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(
      not(
        checked,
        rightChecked.map((i) => i.campaign_shared_uid)
      )
    );
  };

  const handleClose = () => {
    setScopeName(""); // clear scope name
    setError(""); // clear scope name error
    setCampaignError(""); // clear campaign error
    setChecked([]); // clear checked
    setLeft(filteredCampaigns); // reset left with all campaigns
    setRight([]); // empty right
    onClose(); // call parent onClose
  };

  const createNewScope = async (scopeName: string, campaignList: any[]) => {
    if (editScopeLoading || campaignToScopeLoading) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Campaign linking is in progress. Please wait until it’s complete before starting another.",
          type: "warning",
        })
      );
      return;
    }
    const requestParams = {
      name: scopeName,
      ad_account_shared_uid: ad_account_shared_uid,
    };
    const campaignSharedUids = campaignList.map(
      (c: any) => c.campaign_shared_uid
    );

    try {
      handleClose(); // close modal on submit (dont wait for response as it takes time)
      dispatch(setNewScopeLoading(true));
      const createResp = await STOperationEndPointLogic.createNewScope(
        oauthTokenHeader,
        requestParams // Replace with actual query ID
      );

      if (!createResp || createResp.reason !== "success" || !createResp.data) {
        return {
          success: false,
          step: "create",
          error: createResp?.reason || "Failed to create scope",
          rawCreateResponse: createResp,
        };
      }

      if (createResp.reason === "success" && createResp?.data) {
        // Check if campaign list is empty
        if (campaignSharedUids?.length === 0) {
          await scopeList(); // update scope list
          dispatch(setNewScopeLoading(false));
          dispatch(
            setDisplayErrorSnackBar({
              message: "Scope created successfully.",
              type: "success",
            })
          );
          return;
        }

        // After creating new scope then link with campaigns to scope
        const linkReqBody = {
          scope_shared_uid: createResp?.data,
          campaign_shared_uid: campaignSharedUids,
        };
        await linkCampaignToScope(linkReqBody, "AddScope");
      }
    } catch (error) {
      dispatch(setNewScopeLoading(false));
      console.error("Error:", error);
    }
  };

  const handleConfirm = () => {
    const validationError = validateTextField(scopeName, 8);
    if (validationError) {
      setError(validationError);
      return;
    }

    // if (right.length === 0) {
    //   setCampaignError("Please select at least one campaign from the left.");
    //   return;
    // }

    setCampaignError("");
    createNewScope(scopeName, right);
  };

  // === COLUMNS ===
  const columns: GridColDef[] = [
    {
      field: "campaign_name",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "campaign_id",
      headerName: "Campaign ID",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "clicks_last_30_days",
      headerName: "Clicks Last 30 Days",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatNumber(params.value) : ""}
        </Typography>
      ),
    },
    {
      field: "cost_last_30_days",
      headerName: "Cost Last 30 Days",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatCurrency(params.value) : ""}
        </Typography>
      ),
    },
    {
      field: "convertions_last_30_days",
      headerName: "Conversions Last 30 Days",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? Math.round(params.value) : ""}
        </Typography>
      ),
    },
  ];

  const customList = (items: any[]) => (
    <Paper sx={{ width: 350, height: 300, overflow: "auto" }}>
      <List dense component="div" role="list">
        {items.map((value: any) => {
          const labelId = `transfer-list-item-${value.campaign_shared_uid}-label`;

          return (
            <ListItem
              key={value.campaign_shared_uid}
              role="listitem"
              disablePadding
            >
              <ListItemButton
                onClick={handleToggle(value.campaign_shared_uid)}
                disabled={newScopeLoading}
              >
                <ListItemIcon>
                  <Checkbox
                    color="secondary"
                    checked={checked.indexOf(value.campaign_shared_uid) !== -1}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                    inputProps={{ "aria-labelledby": labelId }}
                    disabled={newScopeLoading}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={value.campaign_name} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ padding: "20px 20px 0px 16px" }}>
        Add New Scope
      </DialogTitle>
      <DialogContent sx={{ padding: "0px 20px 16px", position: "relative" }}>
        {/* Scope Name Input */}
        <TextField
          required
          label="Scope Name"
          value={scopeName}
          onChange={(e) => {
            setScopeName(e.target.value);
            const err = validateTextField(e.target.value, 8);
            setError(err);
          }}
          color="secondary"
          sx={{ width: "100%", marginBottom: 3, mt: 2 }}
          size="small"
          error={!!error}
          helperText={
            error || "Minimum of 8 characters, only letters and numbers."
          }
        />

        {/* Transfer List Section */}
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item>
            <Typography gutterBottom color="text.secondary" mb={1.5}>
              Selected Campaigns
            </Typography>
            {customList(left)}
          </Grid>
          <Grid item>
            <Grid container direction="column" alignItems="center">
              <Button
                sx={{ my: 0.5 }}
                color="secondary"
                variant="outlined"
                size="small"
                onClick={handleAllRight}
                disabled={left.length === 0}
              >
                ≫
              </Button>
              <Button
                sx={{ my: 0.5 }}
                color="secondary"
                variant="outlined"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
              >
                &gt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
              >
                &lt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleAllLeft}
                disabled={right.length === 0}
              >
                ≪
              </Button>
            </Grid>
          </Grid>
          <Grid item>
            <Typography gutterBottom color="text.secondary" mb={1.5}>
              Campaigns In new scope
            </Typography>
            {customList(right)}

            {campaignError && (
              <Typography color="error" variant="body2" sx={{ mt: 1, ml: 2 }}>
                {campaignError}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ my: 3, fontStyle: "italic", display: "block" }}
        >
          * The left list shows campaigns not yet assigned to any scope. Use the
          buttons in the middle to move campaigns between the lists. To link a
          campaign to a scope, select it from the left list".
        </Typography>

        {/* table */}
        <Typography sx={{ mt: 3, mb: 1 }} variant="subtitle1">
          Campaign Details
        </Typography>
        <CommonDataGridTable rows={campaignsWithDetails} columns={columns} />
      </DialogContent>

      <DialogActions sx={{ padding: "0px 16px 20px 16px" }}>
        <Button
          onClick={handleClose}
          disabled={newScopeLoading}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          data-tracker="498"
          disabled={!scopeName || !!error || newScopeLoading}
          onClick={handleConfirm}
          color="secondary"
          variant="contained"
          endIcon={<AddIcon />}
        >
          ADD
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddScopeModal;
